import * as yup from 'yup';
import { useMemo } from 'react';
import { useFormik } from 'formik';
import { translate } from 'i18n/jhipster-language';
import { defaultValue } from '@entities/card-product/model';
import { getEntityFromListById } from 'shared/lib/util/entity-utils';
import { dateRangeFieldTest, nonNegativeNumberField, readOnlyTextField, requiredDescriptionField, requiredShortCodeField, requiredTextField, } from './yup-helpers';
/** Unique product code for new rows (backend remains source of truth for uniqueness). */
const generateProductCode = () => `CP-${globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
const mergeWithDefaults = (current) => {
    if (!current) {
        return { ...defaultValue, schemaId: null, cardLimits: [], cardRestrictions: [] };
    }
    const legacy = current;
    return {
        ...defaultValue,
        ...current,
        issuanceFeeAmount: current.issuanceFeeAmount ?? legacy.cardIssuanceFee ?? defaultValue.issuanceFeeAmount,
        replacementFeeAmount: current.replacementFeeAmount ?? legacy.cardReplacementFee ?? defaultValue.replacementFeeAmount,
        cardLimits: current.cardLimits ?? [],
        cardRestrictions: current.cardRestrictions ?? [],
        schemaId: current.schema?.id ?? null,
    };
};
export const form = (props) => {
    const validationSchema = yup.object().shape({
        productCode: readOnlyTextField().required(translate('entity.validation.required')).nullable(),
        productName: requiredTextField().nullable(),
        schemaId: yup
            .number()
            .typeError(translate('entity.validation.number'))
            .required(translate('entity.validation.required')),
        cardType: yup.string().required(translate('entity.validation.required')).nullable(),
        cardForm: yup.string().required(translate('entity.validation.required')).nullable(),
        status: yup.string().required(translate('entity.validation.required')).nullable(),
        description: requiredDescriptionField(),
        displayOrder: nonNegativeNumberField(),
        defaultValidityYears: nonNegativeNumberField(),
        minKycLevel: requiredShortCodeField(),
        maxCardsPerCustomer: nonNegativeNumberField(),
        requiresApprovalForIssuance: yup.boolean().required(translate('entity.validation.required')),
        targetCustomerSegment: requiredTextField(),
        monthlyFeeAmount: nonNegativeNumberField(),
        annualFeeAmount: nonNegativeNumberField(),
        issuanceFeeAmount: nonNegativeNumberField(),
        replacementFeeAmount: nonNegativeNumberField(),
        maintenanceFee: nonNegativeNumberField(),
        effectiveFrom: yup
            .string()
            .required(translate('entity.validation.required'))
            .test(dateRangeFieldTest('effectiveFrom', 'effectiveUntil')),
        effectiveUntil: yup
            .string()
            .required(translate('entity.validation.required'))
            .test(dateRangeFieldTest('effectiveFrom', 'effectiveUntil')),
        atmAllowed: yup.boolean().required(translate('entity.validation.required')),
        posAllowed: yup.boolean().required(translate('entity.validation.required')),
        ecommerceAllowed: yup.boolean().required(translate('entity.validation.required')),
        contactlessAllowed: yup.boolean().required(translate('entity.validation.required')),
        internationalAllowed: yup.boolean().required(translate('entity.validation.required')),
        cashWithdrawalAllowed: yup.boolean().required(translate('entity.validation.required')),
        interchangeScope: yup.string().required(translate('entity.validation.required')),
        authorizationRequirement: yup.string().required(translate('entity.validation.required')),
        usageRestriction: yup.string().required(translate('entity.validation.required')),
        commissionGroupCode: requiredTextField(),
        rewardGroupCode: requiredTextField(),
        pricingGroupCode: requiredTextField(),
    });
    const initialValues = useMemo(() => {
        const merged = mergeWithDefaults(props.current);
        if (props.current?.id != null) {
            return merged;
        }
        const code = merged.productCode?.trim() ? merged.productCode : generateProductCode();
        return { ...merged, productCode: code };
    }, [props.current?.id, props.current?.productCode]);
    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit(data) {
            const { schemaId, schema: _prevSchema, ...rest } = data;
            if (schemaId == null) {
                return;
            }
            const schema = getEntityFromListById(schemaId, [...props.cardSchemas]) ?? props.current?.schema ?? null;
            if (!schema) {
                return;
            }
            const entity = {
                ...rest,
                schema,
                id: data.id,
            };
            const payload = props.current?.id ? { ...props.current, ...entity } : entity;
            if (payload.maintenanceFee === 0) {
                delete payload.maintenanceFee;
            }
            if (props.current?.id) {
                props.updateEntity(payload);
            }
            else {
                props.createEntity(payload);
            }
        },
    });
    return formik;
};
